import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useRetryTataEvent, useSystemHealthPreIfr } from "../../lib/api";
import { Chip, FailedToLoad, Loading, Notice, PAGE, Pager, focusRing, panel, pillClass, pillStyle } from "./healthUi";
import { formatWhen } from "./healthFormat";
import { SecondaryButton } from "./primitives";

// Pre-IFR Agent tab: one log of everything the journey receives and sends. Built by the API from rows
// it already keeps (apps/api/pre_ifr_log.py); phone numbers arrive cut to their last four digits.

const TONES = { ok: "good", warning: "warn", problem: "bad", info: "idle" };
const STATUS_WORDS = { ok: "OK", warning: "Check", problem: "Problem", info: "Info" };
const KIND_HINTS = {
  tata_in: "What Tata sent us",
  bot_in: "Calls from the Chat360 bot",
  template: "WhatsApp templates we sent",
  tata_out: "Events we sent to Tata",
  journey: "What happened on the job card",
};

const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]";
const td = "px-3 py-2.5 align-top text-[13px] text-[#374151]";

function Facts({ items }) {
  const shown = items.filter(([, value]) => value !== undefined && value !== null && value !== "");
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-[12px] sm:grid-cols-2">
      {shown.map(([label, value]) => (
        <div key={label} className="flex gap-2">
          <dt className="w-[110px] flex-none text-[#6B7280]">{label}</dt>
          <dd className="min-w-0 break-words text-[#111827]">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

// The bot's variables, each marked as filled, empty or never filled in, with what is wrong above them.
function BotVariables({ detail }) {
  const report = detail.report;
  const variables = detail.variables || {};
  const names = Object.keys(variables);
  const state = (name) =>
    report?.unfilled?.includes(name) ? "never filled in" : report?.empty?.includes(name) ? "empty" : "filled";
  return (
    <div data-testid="bot-variables">
      {report?.warnings?.length > 0 && (
        <ul className="mb-2 space-y-1" data-testid="bot-warnings">
          {report.warnings.map((w) => (
            <li key={w} className="text-[12px] font-medium text-[#B54708]">
              {w}
            </li>
          ))}
        </ul>
      )}
      {names.length ? (
        <table className="text-[12px]">
          <tbody>
            {names.map((name) => (
              <tr key={name}>
                <td className="pr-4 font-medium text-[#111827]">{name}</td>
                <td className="pr-4 text-[#374151]">{variables[name] === "" || variables[name] === null ? "—" : String(variables[name])}</td>
                <td className={state(name) === "filled" ? "text-[#166534]" : "text-[#B42318]"}>{state(name)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-[12px] text-[#6B7280]">No variables were stored for this call.</div>
      )}
    </div>
  );
}

// An event we sent to Tata that was not delivered can be sent again from here.
function SendAgain({ entry, tenantId, onSent }) {
  const retry = useRetryTataEvent(tenantId);
  const go = async () => {
    try {
      await retry.mutateAsync(entry.detail.row_id);
      toast.success("Queued to send again.");
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send it again.");
    }
  };
  return (
    <SecondaryButton onClick={go} disabled={retry.isPending}>
      Send again
    </SecondaryButton>
  );
}

function Detail({ entry, tenantId, onSent }) {
  const d = entry.detail || {};
  const payload = d.payload;
  return (
    <div className="space-y-3 bg-[#F9FAFB] px-4 py-3" data-testid={`detail-${entry.id}`}>
      {entry.kind === "bot_in" && (
        <>
          <Facts
            items={[
              ["Result", d.status], ["Why", d.reason], ["Customer", d.customer], ["Replied to", d.message_id],
              ["Event id", d.event_id], ["Bot key", d.connection_key], ["From", d.source_ip],
              ["Phone sent", d.report ? (d.report.has_phone ? "yes" : "no") : undefined],
            ]}
          />
          {d.report && <BotVariables detail={d} />}
        </>
      )}
      {entry.kind === "tata_in" && (
        <Facts items={[["Event", d.event_type], ["Result", d.status], ["Why", d.reason], ["Event id", d.event_id], ["From", d.source_ip]]} />
      )}
      {entry.kind === "template" && (
        <Facts
          items={[
            ["Operation", d.operation], ["HTTP", d.http_status], ["Error", d.error_code], ["Why", d.error],
            ["WhatsApp id", d.message_id], ["Took", d.duration_ms != null ? `${d.duration_ms} ms` : undefined], ["Attempt", d.attempt],
          ]}
        />
      )}
      {entry.kind === "tata_out" && (
        <Facts
          items={[
            ["Result", d.status], ["Tata answered", d.response_status], ["Answer", d.response_body], ["Why", d.reason],
            ["Tries", d.attempts], ["Next try", d.next_attempt_at ? formatWhen(d.next_attempt_at) : undefined], ["Event id", d.event_id],
          ]}
        />
      )}
      {entry.kind === "tata_out" && d.status !== "sent" && d.row_id != null && tenantId != null && (
        <SendAgain entry={entry} tenantId={tenantId} onSent={onSent} />
      )}
      {entry.kind === "journey" && <Facts items={Object.entries(d).map(([k, v]) => [k, typeof v === "object" ? JSON.stringify(v) : v])} />}
      {payload && (
        <details>
          <summary className={`cursor-pointer text-[12px] font-medium text-[#2667B5] ${focusRing}`}>What was sent</summary>
          <pre className="mt-2 max-h-[260px] overflow-auto rounded-lg border border-[#E5E7EB] bg-white p-3 text-[11px] text-[#374151]">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

// The log is one brand's: with "All brands" picked there is nothing to read yet.
export default function HealthPreIfr(props) {
  if (props.scope.needsBrand) {
    return <Notice>Choose a brand at the top of the page: the Pre-IFR log is one brand&apos;s.</Notice>;
  }
  return <PreIfrLog {...props} />;
}

function PreIfrLog({ scope, range }) {
  const [kind, setKind] = useState("");
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [problems, setProblems] = useState(false);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(typed.trim()), 300);
    return () => clearTimeout(t);
  }, [typed]);
  useEffect(() => {
    setPage(0);
    setOpen(null);
  }, [kind, search, problems, scope.tenantId, range?.key]);

  const query = useSystemHealthPreIfr({ ...scope, from: range.from(), kind, search, problems, limit: PAGE, offset: page * PAGE });
  const data = query.data;

  if (query.isError && !data) return <FailedToLoad error={query.error} retry={() => query.refetch()} />;
  if (!data) return <Loading />;

  const counts = data.counts || { kinds: {}, problems: 0, total: 0 };
  const pill = (key, label, n) => (
    <button
      key={key || "all"}
      type="button"
      aria-pressed={kind === key}
      data-testid={`kind-${key || "all"}`}
      onClick={() => setKind(key)}
      className={`${pillClass} whitespace-nowrap`}
      style={pillStyle(kind === key)}
      title={KIND_HINTS[key] || "Everything"}
    >
      {label} {n}
    </button>
  );

  return (
    <div data-testid="health-pre-ifr">
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {pill("", "All", counts.total)}
        {(data.kinds || []).map((k) => pill(k.key, k.label, counts.kinds?.[k.key] || 0))}
        <label className="ml-auto flex items-center gap-2 text-[12px] text-[#4B5563]">
          <input type="checkbox" checked={problems} onChange={(e) => setProblems(e.target.checked)} aria-label="Problems only" />
          Problems only <span className="font-semibold">{counts.problems}</span>
        </label>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Search job card, template, event"
          aria-label="Search the log"
          className="h-9 w-[260px] max-w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-[13px] outline-none focus:border-[#9CA3AF]"
        />
      </div>

      <div className={`${panel} mt-4 overflow-x-auto`}>
        {data.rows.length === 0 ? (
          <div data-testid="log-empty" className="py-12 text-center text-[13px] text-[#6B7280]">
            {counts.total === 0 && !search && !problems ? "Nothing has been logged in this period." : "Nothing matches."}
          </div>
        ) : (
          <table className="w-full min-w-[720px]" data-testid="log-table">
            <thead>
              <tr>
                <th className={th}>When</th>
                <th className={th}>Kind</th>
                <th className={th}>What</th>
                <th className={th}>Job card</th>
                <th className={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((e) => (
                <FragmentRow key={e.id} entry={e} open={open === e.id} toggle={() => setOpen(open === e.id ? null : e.id)} kinds={data.kinds} tenantId={scope.tenantId} onSent={() => query.refetch()} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data.rows.length > 0 && <Pager page={page} shown={data.rows.length} total={data.total} onPage={setPage} />}
      {data.truncated && (
        <p data-testid="log-truncated" className="mt-3 text-[12px] text-[#6B7280]">
          This period has more than the log reads at once; showing the newest. Narrow the period to see older entries.
        </p>
      )}
    </div>
  );
}

function FragmentRow({ entry, open, toggle, kinds, tenantId, onSent }) {
  const label = (kinds || []).find((k) => k.key === entry.kind)?.label || entry.kind;
  return (
    <>
      <tr
        className="cursor-pointer border-t border-[#F3F4F6] hover:bg-[#F9FAFB]"
        data-testid={`log-row-${entry.id}`}
        onClick={toggle}
        aria-expanded={open}
      >
        <td className={`${td} whitespace-nowrap`}>{formatWhen(entry.at)}</td>
        <td className={`${td} whitespace-nowrap text-[#6B7280]`}>{label}</td>
        <td className={td}>
          <div className="font-medium text-[#111827]">{entry.title}</div>
          {entry.summary && <div className="text-[12px] text-[#6B7280]">{entry.summary}</div>}
        </td>
        <td className={`${td} whitespace-nowrap`}>{entry.jc_id || "—"}</td>
        <td className={td}>
          <Chip tone={TONES[entry.status] || "idle"}>{STATUS_WORDS[entry.status] || entry.status}</Chip>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="p-0">
            <Detail entry={entry} tenantId={tenantId} onSent={onSent} />
          </td>
        </tr>
      )}
    </>
  );
}
